import { useEffect, useMemo, useState } from "react";
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Sprout } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import DemandMap from "@/components/dashboard/DemandMap";

const nz = new Intl.NumberFormat("en-NZ");

const SEVERITY_STYLE = {
    high:   { bg: "bg-[#ef4444]/10", border: "border-[#ef4444]/40", dot: "#ef4444", text: "text-[#fecaca]" },
    medium: { bg: "bg-[#f59e0b]/10", border: "border-[#f59e0b]/40", dot: "#f59e0b", text: "text-[#fde68a]" },
    low:    { bg: "bg-[#22C55E]/5",  border: "border-[#22C55E]/30", dot: "#22C55E", text: "text-[#86efac]" },
};

export default function SuppliersDashboard() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [hovered, setHovered] = useState(null);
    const [countryFilter, setCountryFilter] = useState("ALL");

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get("/metrics/suppliers");
                if (!cancel) setData(data);
            } catch (e) {
                if (!cancel)
                    setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
            }
        })();
        return () => {
            cancel = true;
        };
    }, []);

    const filteredRegions = useMemo(() => {
        if (!data) return [];
        if (countryFilter === "ALL") return data.demand_regions;
        return data.demand_regions.filter((r) => r.country === countryFilter);
    }, [data, countryFilter]);

    if (err) {
        return (
            <div className="p-8">
                <div className="hg-card p-5 border-[#ef4444]/40">
                    <div className="hg-eyebrow text-[#fecaca]">// Error</div>
                    <div className="font-mono-data mt-1">{err}</div>
                </div>
            </div>
        );
    }

    if (!data) return <SkeletonSuppliers />;

    const hoveredRegion =
        hovered && data.demand_regions.find((r) => r.region === hovered);

    return (
        <div
            className="min-h-full px-4 md:px-8 py-6 md:py-8"
            data-testid="suppliers-dashboard"
        >
            {/* Header */}
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pb-6 border-b border-white/5">
                <div>
                    <div className="hg-eyebrow mb-2">// Section 02 · Suppliers</div>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#FAFAFA] leading-none">
                        Supplier Intelligence
                    </h1>
                    <p className="mt-2 text-sm text-[#A1A1AA]">
                        Live crop demand, regional heatmap and surplus alerts across NZ & AU.
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <CountryFilter value={countryFilter} onChange={setCountryFilter} />
                    <div className="hidden md:flex items-center gap-2 hg-eyebrow">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] hg-pulse-dot" />
                        Updated {new Date(data.updated_at).toLocaleTimeString()}
                    </div>
                </div>
            </header>

            {/* Totals strip */}
            <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                <StatTile
                    label="// Regions tracked"
                    value={data.totals.regions_tracked}
                    testid="stat-regions-tracked"
                />
                <StatTile
                    label="// High-demand regions"
                    value={data.totals.high_demand_regions}
                    accent
                    testid="stat-high-demand"
                />
                <StatTile
                    label="// Surplus alerts"
                    value={data.totals.surplus_alert_count}
                    danger={data.totals.surplus_alert_count > 0}
                    testid="stat-surplus-count"
                />
            </section>

            {/* Map + top 10 side-by-side */}
            <section className="grid grid-cols-1 lg:grid-cols-6 gap-4 mt-4">
                <div
                    className="hg-card p-5 lg:col-span-4 flex flex-col"
                    data-testid="demand-heatmap"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="hg-eyebrow">// Demand Heatmap</div>
                            <div className="font-heading text-xl text-[#FAFAFA] mt-1">
                                NZ · AU · live demand
                            </div>
                        </div>
                        {hoveredRegion && (
                            <div className="hidden md:block text-right">
                                <div className="hg-eyebrow">
                                    // {hoveredRegion.country} · {hoveredRegion.code}
                                </div>
                                <div className="font-mono-data text-lg text-[#FAFAFA]">
                                    {hoveredRegion.region}
                                </div>
                                <div className="font-mono-data text-xs text-[#22C55E]">
                                    Score {hoveredRegion.demand_score}/100
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="relative mt-3 h-[320px] md:h-[460px] w-full">
                        <DemandMap
                            regions={filteredRegions}
                            onHover={setHovered}
                            hovered={hovered}
                        />
                    </div>
                </div>

                <div
                    className="hg-card p-5 lg:col-span-2 flex flex-col"
                    data-testid="top-10-crops"
                >
                    <div className="flex items-center justify-between">
                        <div className="hg-eyebrow">// Top 10 crops · demand</div>
                        <Sprout size={14} className="text-[#22C55E]" />
                    </div>
                    <ul className="mt-4 flex-1 flex flex-col gap-1.5">
                        {data.top_10_crops.map((c) => (
                            <li
                                key={c.rank}
                                className="group flex items-center gap-3 px-2 py-2 border-l-2 border-transparent hover:border-[#22C55E] hover:bg-white/[0.02] transition-colors"
                                data-testid={`top-crop-${c.rank}`}
                            >
                                <span className="font-mono-data text-[10px] text-[#52525b] w-5">
                                    {String(c.rank).padStart(2, "0")}
                                </span>
                                <span className="flex-1 font-mono-data text-xs text-[#FAFAFA] uppercase tracking-wider">
                                    {c.crop}
                                </span>
                                <span className="font-mono-data text-[11px] text-[#A1A1AA] group-hover:text-[#FAFAFA]">
                                    {nz.format(c.volume_units)}
                                </span>
                                <TrendPill trend={c.trend} changePct={c.change_pct} />
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Most requested per region + Surplus alerts */}
            <section className="grid grid-cols-1 lg:grid-cols-6 gap-4 mt-4">
                <div
                    className="hg-card p-5 lg:col-span-4"
                    data-testid="week-requests"
                >
                    <div className="flex items-center justify-between pb-3 border-b border-white/5">
                        <div>
                            <div className="hg-eyebrow">// Most requested · this week</div>
                            <div className="font-heading text-xl text-[#FAFAFA] mt-1">
                                Top crop by region
                            </div>
                        </div>
                        <div className="hg-eyebrow text-[10px]">
                            {data.week_requests.length} regions
                        </div>
                    </div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                        {data.week_requests.map((w, i) => {
                            const max = data.week_requests[0].requests;
                            const pct = (w.requests / max) * 100;
                            return (
                                <div
                                    key={w.region}
                                    className="flex items-center gap-3 py-2 border-b border-white/[0.03]"
                                    data-testid={`week-request-${w.code.toLowerCase()}`}
                                >
                                    <span className="font-mono-data text-[10px] text-[#52525b] w-4 text-right">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="hg-eyebrow text-[10px] w-8 shrink-0">
                                        {w.code}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline justify-between gap-2">
                                            <span className="font-mono-data text-xs text-[#FAFAFA] truncate">
                                                {w.top_crop}
                                            </span>
                                            <span className="font-mono-data text-[11px] text-[#22C55E]">
                                                {nz.format(w.requests)}
                                            </span>
                                        </div>
                                        <div className="h-[2px] mt-1 bg-white/5 overflow-hidden rounded-full">
                                            <div
                                                className="h-full bg-[#22C55E]"
                                                style={{
                                                    width: `${pct}%`,
                                                    boxShadow: "0 0 6px rgba(34,197,94,0.5)",
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div
                    className="hg-card p-5 lg:col-span-2"
                    data-testid="surplus-alerts"
                >
                    <div className="flex items-center justify-between pb-3 border-b border-white/5">
                        <div className="flex items-center gap-2">
                            <AlertTriangle size={14} className="text-[#f59e0b]" />
                            <span className="hg-eyebrow">// Surplus alerts</span>
                        </div>
                        <div className="hg-eyebrow text-[10px] text-[#f59e0b]">
                            {data.surplus_alerts.length} active
                        </div>
                    </div>
                    <div className="mt-3 space-y-2">
                        {data.surplus_alerts.map((a, i) => {
                            const s = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.low;
                            return (
                                <div
                                    key={i}
                                    className={`${s.bg} border ${s.border} rounded-sm px-3 py-2.5`}
                                    data-testid={`surplus-${a.region.toLowerCase()}-${a.crop.toLowerCase().replace(/\s/g, "-")}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="w-1.5 h-1.5 rounded-full"
                                                style={{ background: s.dot }}
                                            />
                                            <span className="hg-eyebrow text-[10px]">
                                                {a.country} · {a.region}
                                            </span>
                                        </div>
                                        <span
                                            className={`font-mono-data text-[11px] uppercase tracking-wider ${s.text}`}
                                        >
                                            {a.severity}
                                        </span>
                                    </div>
                                    <div className="mt-1.5 flex items-baseline justify-between">
                                        <span className="font-mono-data text-sm text-[#FAFAFA]">
                                            {a.crop}
                                        </span>
                                        <span className="font-mono-data text-sm text-[#f59e0b]">
                                            +{a.surplus_pct}%
                                        </span>
                                    </div>
                                    <div className="hg-eyebrow mt-1 text-[9px] text-[#71717a]">
                                        Supply outpacing demand
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
}

const CountryFilter = ({ value, onChange }) => (
    <div
        className="flex items-center border border-white/10 rounded-sm overflow-hidden"
        data-testid="country-filter"
    >
        {[
            { k: "ALL", label: "ALL" },
            { k: "NZ",  label: "NZ"  },
            { k: "AU",  label: "AU"  },
        ].map((o) => (
            <button
                key={o.k}
                onClick={() => onChange(o.k)}
                className={`px-3 py-1.5 font-mono-data text-[11px] uppercase tracking-widest transition-colors ${
                    value === o.k
                        ? "bg-[#22C55E] text-[#050505]"
                        : "text-[#A1A1AA] hover:text-[#FAFAFA]"
                }`}
                data-testid={`country-filter-${o.k.toLowerCase()}`}
            >
                {o.label}
            </button>
        ))}
    </div>
);

const StatTile = ({ label, value, accent, danger, testid }) => (
    <div className="hg-card p-5" data-testid={testid}>
        <div className="hg-eyebrow">{label}</div>
        <div
            className={`font-mono-data text-3xl md:text-4xl mt-2 ${
                accent
                    ? "text-[#22C55E] hg-glow-sm"
                    : danger
                    ? "text-[#f59e0b]"
                    : "text-[#FAFAFA]"
            }`}
        >
            {value}
        </div>
    </div>
);

const TrendPill = ({ trend, changePct }) => {
    const map = {
        up:     { Icon: TrendingUp,   color: "#22C55E" },
        down:   { Icon: TrendingDown, color: "#ef4444" },
        stable: { Icon: Minus,        color: "#A1A1AA" },
    };
    const { Icon, color } = map[trend] || map.stable;
    return (
        <span
            className="flex items-center gap-1 font-mono-data text-[10px] min-w-[48px] justify-end"
            style={{ color }}
        >
            <Icon size={11} />
            {changePct > 0 ? "+" : ""}
            {changePct.toFixed(1)}%
        </span>
    );
};

const SkeletonSuppliers = () => (
    <div className="p-8 space-y-4">
        <div className="h-12 w-80 bg-white/5 rounded-sm animate-pulse" />
        <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="hg-card h-[110px] p-5">
                    <div className="h-3 w-24 bg-white/5 rounded-sm animate-pulse" />
                    <div className="mt-4 h-8 w-16 bg-white/5 rounded-sm animate-pulse" />
                </div>
            ))}
        </div>
        <div className="hg-card h-[520px] animate-pulse" />
    </div>
);
